/**
 * Timezone Conversion Utilities
 *
 * Utilities for converting UTC timestamps from backend to custom timezone
 * for display in charts and UI components.
 *
 * Supports both browser local timezone and custom timezone selection.
 */

/**
 * Common timezone options for the selector
 */
export const COMMON_TIMEZONES = [
  { value: 'auto', label: 'Auto (Browser Local)', offset: null },
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: -5 },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: -6 },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: -7 },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: -8 },
  { value: 'America/Phoenix', label: 'Arizona (MST)', offset: -7 },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', offset: -9 },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)', offset: -10 },
  { value: 'UTC', label: 'UTC', offset: 0 },
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: 0 },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: 1 },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: 9 },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', offset: 8 },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: 4 },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', offset: 10 }
];

/**
 * Convert UTC timestamp string to JavaScript Date object
 * Backend returns timestamps in format: "2026-06-26 00:00:00" (UTC)
 * This function parses it as UTC and returns a Date object.
 *
 * @param {string} utcTimeString - UTC timestamp string from backend
 * @returns {Date} - JavaScript Date object (in UTC)
 */
export const parseUTCTimestamp = (utcTimeString) => {
  if (!utcTimeString) return null;

  // Replace space with 'T' and add 'Z' to explicitly mark as UTC
  // "2026-06-26 00:00:00" -> "2026-06-26T00:00:00Z"
  const isoString = utcTimeString.replace(' ', 'T') + 'Z';
  return new Date(isoString);
};

/**
 * Convert UTC Date to target timezone (returns adjusted timestamp)
 * Since browsers always display dates in their local timezone, we adjust the timestamp
 * so that when displayed locally, it shows the correct time for the target timezone.
 *
 * @param {Date} utcDate - UTC Date object
 * @param {string} targetTimezone - IANA timezone identifier
 * @returns {Date} - Date object adjusted to target timezone
 */
/**
 * Get the actual offset for a timezone at a specific date (accounts for DST)
 * @param {string} timezone - IANA timezone identifier
 * @param {Date} date - Date to check offset for
 * @returns {number} - Offset in minutes
 */
export const getTimezoneOffset = (timezone, date) => {
  // Use formatToParts to get the date components in the target timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date);
  const get = (type) => {
    const part = parts.find(p => p.type === type);
    return part ? parseInt(part.value, 10) : 0;
  };

  // Create a UTC timestamp from the target timezone's local time components
  const tzAsUTC = Date.UTC(
    get('year'),
    get('month') - 1,  // month is 0-indexed in Date.UTC
    get('day'),
    get('hour'),
    get('minute'),
    get('second')
  );

  // The offset is the difference between the UTC timestamp of the local time
  // and the actual UTC timestamp
  return (tzAsUTC - date.getTime()) / (60 * 1000);
};

export const convertToTimezone = (utcDate, targetTimezone) => {
  if (!utcDate || !targetTimezone || targetTimezone === 'auto') {
    return utcDate; // Return as-is for browser local timezone
  }

  // Find the target timezone info
  const tzInfo = COMMON_TIMEZONES.find(tz => tz.value === targetTimezone);
  if (!tzInfo) {
    return utcDate; // If timezone not found, return as-is
  }

  // Get actual timezone offset for this specific date (accounts for DST)
  const targetOffsetMinutes = getTimezoneOffset(targetTimezone, utcDate);

  // Get browser's local timezone offset in minutes
  const browserOffsetMinutes = utcDate.getTimezoneOffset();

  // Calculate the difference: how many minutes to shift
  // getTimezoneOffset returns positive for west of UTC, negative for east
  // getTimezoneOffset returns the amount UTC is ahead, so negate it
  const offsetDiffMinutes = targetOffsetMinutes + browserOffsetMinutes;

  // Adjust the timestamp
  const adjustedTimestamp = utcDate.getTime() + (offsetDiffMinutes * 60 * 1000);

  return new Date(adjustedTimestamp);
};

/**
 * Convert time series data array from UTC timestamps to Date objects
 * Transforms array of {time: "2026-06-26 00:00:00", value: X} objects
 * to use JavaScript Date objects that ECharts can display
 *
 * @param {Array} timeSeriesArray - Array of {time, value} objects
 * @param {string} targetTimezone - Target timezone for display
 * @returns {Array} - Transformed array with Date objects
 */
export const convertTimeSeriesData = (timeSeriesArray, targetTimezone = 'auto') => {
  if (!timeSeriesArray || !Array.isArray(timeSeriesArray)) {
    return [];
  }

  return timeSeriesArray.map(item => {
    const utcDate = parseUTCTimestamp(item.time);
    return {
      ...item,
      time: convertToTimezone(utcDate, targetTimezone)
    };
  });
};

/**
 * Convert all time series fields in analytics response
 * Applies timezone conversion to all time-based data in the dashboard response
 *
 * @param {Object} analyticsData - Full analytics response from backend
 * @param {string} targetTimezone - Target timezone for conversion ('auto' for browser local)
 * @returns {Object} - Analytics data with converted timestamps
 */
export const convertAnalyticsTimestamps = (analyticsData, targetTimezone = 'auto') => {
  if (!analyticsData) return null;

  const converted = { ...analyticsData };

  // Convert time_series data
  if (converted.time_series) {
    converted.time_series = {
      calls_over_time: convertTimeSeriesData(converted.time_series.calls_over_time, targetTimezone),
      duration_over_time: convertTimeSeriesData(converted.time_series.duration_over_time, targetTimezone),
      success_rate_over_time: convertTimeSeriesData(converted.time_series.success_rate_over_time, targetTimezone),
      transfer_rate_over_time: convertTimeSeriesData(converted.time_series.transfer_rate_over_time, targetTimezone),
      picked_up_rate_over_time: convertTimeSeriesData(converted.time_series.picked_up_rate_over_time, targetTimezone),
      voicemail_rate_over_time: convertTimeSeriesData(converted.time_series.voicemail_rate_over_time, targetTimezone),
      latency_over_time: convertTimeSeriesData(converted.time_series.latency_over_time, targetTimezone)
    };
  }

  // Convert concurrency_over_time data
  if (converted.concurrency_over_time) {
    converted.concurrency_over_time = convertTimeSeriesData(converted.concurrency_over_time, targetTimezone);
  }

  return converted;
};

/**
 * Get user's timezone name (e.g., "America/New_York")
 * @returns {string} - IANA timezone identifier
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get user's timezone offset in hours (e.g., -5 for EST)
 * @returns {number} - Offset in hours
 */
export const getUserTimezoneOffset = () => {
  const offsetMinutes = new Date().getTimezoneOffset();
  return -offsetMinutes / 60; // Negative because getTimezoneOffset returns opposite sign
};

/**
 * Format timezone offset as string (e.g., "UTC-5" or "UTC+2")
 * @param {string} timezone - Timezone identifier (optional, defaults to browser local)
 * @returns {string} - Formatted timezone offset
 */
export const getFormattedTimezoneOffset = (timezone = null) => {
  if (!timezone || timezone === 'auto') {
    const offset = getUserTimezoneOffset();
    const sign = offset >= 0 ? '+' : '';
    return `UTC${sign}${offset}`;
  }

  // Get offset for specific timezone
  const timezoneInfo = COMMON_TIMEZONES.find(tz => tz.value === timezone);
  if (timezoneInfo?.offset !== null) {
    const sign = timezoneInfo.offset >= 0 ? '+' : '';
    return `UTC${sign}${timezoneInfo.offset}`;
  }

  return timezone;
};

/**
 * Get display label for timezone
 * @param {string} timezone - Timezone identifier
 * @returns {string} - Human-readable timezone label
 */
export const getTimezoneLabel = (timezone) => {
  if (!timezone || timezone === 'auto') {
    const browserTz = getUserTimezone();
    const offset = getFormattedTimezoneOffset();
    return `${browserTz} (${offset})`;
  }

  const timezoneInfo = COMMON_TIMEZONES.find(tz => tz.value === timezone);
  return timezoneInfo ? timezoneInfo.label : timezone;
};
